import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiContext,
  useGridSelector,
  gridPaginationModelSelector,
  gridSortedRowIdsSelector,
  gridRowSelectionStateSelector,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedRecords, setSelectedProvider } from "../../pages/DisplayRecordsPage/displayRecordsSlice";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import Typography from "@mui/material/Typography";
import { setHeaderMapping } from "../../state/Match/matchSlice";
import { resetUploadStatus } from "../../state/FileUpload/fileUploadSlice";
import { getJobDetails } from "../../state/FileUpload/fileUploadSlice";
import { useParams } from "react-router-dom";
import { AddressProvider } from "../../pages/DisplayRecordsPage/DisplayRecordsPage.constant";

const exportToCSV = (rows, columns, filename) => {
  const selectedColumns = columns.filter(
    (column) =>
      column.field === "firstName" ||
      column.field === "lastName" ||
      column.field === "fullName" ||
      column.field === "imcId" ||
      column.field === "oneKeyId" ||
      column.field === "wpName" ||
      column.field === "address3" ||
      column.field === "city" ||
      column.field === "state" ||
      column.field === "postCode" ||
      column.field === "specialty_1" ||
      column.field === "fax" ||
      column.field === "phone1" ||
      column.field === "phone2" ||
      column.field === "hCP_ID" ||
      column.field === "jobId" ||
      column.field === "email" ||
      column.field === "address1" ||
      column.field === "address2" ||
      column.field === "title" ||
      column.field === "status" ||
      column.field === "onekeyStatus"  ||
      column.field == "gender_code" ||
      column.field == "professional_type" ||
      column.field == "specialty_2" ||
      column.field === "specialty_3" ||
      column.field ===  "parent_Account_ID" ||
      column.field === "account_ID" ||
      column.field === "parent_workplace_name" ||
      column.field === "workplace_type" ||
      column.field === "clients_comment" ||
      column.field === "last_call_date" ||
      column.field === "phone2" ||
      column.field === "position" ||
      column.field === "web_site" ||
      column.field === "full_address"
  );

  const csvRows = [];
  const headers = selectedColumns.map((col) => `"${col.headerName}"`);
  csvRows.push(headers.join(","));

  rows?.forEach((row) => {

    const values = selectedColumns.map((col) => {
      let value = row[col.field];
      
      if (typeof value === "string") {
        value = value.replace(/"/g, '""'); 
        return `"${value}"`; 
      }
      
      return value;
    });
    
    csvRows.push(values.join(","));
  });

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("hidden", "");
  a.setAttribute("href", url);
  a.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Header checkbox for the selection column: toggles only rows on the current
 * pagination page (sorted/filtered order), not the full dataset.
 * Uses Community APIs only — no DataGridPro / license.
 */
function PageSelectAllCheckboxHeader() {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowSelectionModel = useGridSelector(apiRef, gridRowSelectionStateSelector);
  const sortedRowIds = useGridSelector(apiRef, gridSortedRowIdsSelector);

  const { page, pageSize } = paginationModel;
  const start = page * pageSize;
  const pageRowIds = sortedRowIds.slice(start, start + pageSize);

  const selectedOnPage = pageRowIds.filter((id) =>
    rowSelectionModel.includes(id)
  ).length;
  const allSelected =
    pageRowIds.length > 0 && selectedOnPage === pageRowIds.length;
  const indeterminate =
    selectedOnPage > 0 && selectedOnPage < pageRowIds.length;

  const handleChange = () => {
    const api = apiRef.current;
    if (!api) return;
    const current = [...gridRowSelectionStateSelector(api.state)];
    const pageSet = new Set(pageRowIds);
    if (allSelected) {
      api.setRowSelectionModel(current.filter((id) => !pageSet.has(id)));
    } else {
      api.setRowSelectionModel([...new Set([...current, ...pageRowIds])]);
    }
  };

  return (
    <Checkbox
      size="small"
      checked={allSelected}
      indeterminate={indeterminate}
      onChange={handleChange}
      sx={{ p: 0.5 }}
      inputProps={{
        "aria-label": "Select all rows on this page",
      }}
    />
  );
}

function CustomToolbar({
  rows,
  columns,
  handleClickOpen,
  handleGetOneKeyStatus,
  handleRefreshRecords,
  completeJob,
  validateAddress,
  sendAllJobs,
}) {
  const [open, setOpen] = useState(false);
  const [filename, setFilename] = useState("");
  const { token } = useSelector((state) => state.login);
  const { id } = useParams();

  const handleExportClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (filename) {
      exportToCSV(rows, columns, filename);
      setOpen(false);
    }
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateToFileImport = () => {
    dispatch(setHeaderMapping([]));
    dispatch(resetUploadStatus());
    dispatch(getJobDetails({ jobId: id, token: token }));
    navigate("/custome-rmatch-portal/fileUpload", { state: { isDropdownDisabled: true } });
  };

  const onValidateAddress = (selectedProvider) => {
   
      dispatch(setSelectedProvider(selectedProvider));
    validateAddress();
  };

  return (
    <GridToolbarContainer>
      <Stack
        spacing={2}
        direction="row"
        sx={{ justifyContent: "space-between", width: "100%" }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <GridToolbarColumnsButton style={{ color: "#ab1368" }} />
          {/* <GridToolbarDensitySelector/>
          <Tooltip title="Send selected records to Onekey">
            <Button onClick={handleClickOpen}>AUTO-MATCH</Button>
          </Tooltip>
          <Tooltip title="Request Validation Status from Onekey">
            <Button variant="text" onClick={handleGetOneKeyStatus}>
              MANUAL-MATCH
            </Button>
          </Tooltip> */}
          <Tooltip title="Navigate to re-upload exported file">
            <Button
              onClick={navigateToFileImport}
              startIcon={<FileUploadIcon />}
            >
              IMPORT
            </Button>
          </Tooltip>
          <Tooltip title="Export the data to CSV for adjustments and re-upload">
            <Button
              onClick={handleExportClick}
              startIcon={<DownloadIcon />}
              sx={{ color: "#1976d2" }}
            >
              EXPORT
            </Button>
          </Tooltip>
          <Tooltip title="Mark the job as completed">
            <Button variant="text" onClick={completeJob}>
              COMPLETE JOB
            </Button>
          </Tooltip>
          <Tooltip title="Validate address using google api's">
            <Button variant="text" onClick={()=>{onValidateAddress(AddressProvider.Google)}}>
              Validate Address
            </Button>
          </Tooltip>
           <Tooltip title="Validate address using AI api's">
            <Button variant="text" onClick={()=>{onValidateAddress(AddressProvider.AI)}}>
              AI Match
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ marginLeft: "auto" }}>
          <Tooltip title="Refresh all records">
            <Button
              variant="text"
              onClick={handleRefreshRecords}
              startIcon={<RefreshIcon sx={{ color: "#1976d2" }} />}
            >
              REFRESH
            </Button>
          </Tooltip>
        </Box>
      </Stack>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Export CSV</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter a filename for the exported CSV.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Filename"
            type="text"
            fullWidth
            variant="standard"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirm}>Export</Button>
        </DialogActions>
      </Dialog>
    </GridToolbarContainer>
  );
}

const RecordDisplayGrid = ({
  rows,
  columns,
  handleClickOpen,
  handleGetOneKeyStatus,
  handleRefreshRecords,
  completeJob,
  validateAddress,
  sendAllJobs,
  columnVisibilityModel,
  setColumnVisibilityModel,
}) => {
  const dispatch = useDispatch();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [locallyMatchedCount, setLocallyMatchedCount] = useState(0);
  const [oneKeyMatchedCount, setOneKeyMatchedCount] = useState(0);
  const [manualMatchedCount, setManualMatchedCount] = useState(0);
  const [validationPendingCount, setValidationPendingCount] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  useEffect(() => {
    const matched = rows.filter(
      (row) => row.oneKeyId && row.oneKeyId.trim() !== ""
    ).length;
    // const oneKeyMatched = rows.filter(
    //   (row) => row.oneKeyId && row.onekeyRequestId
    // ).length;
    const oneKeyMatched = rows.filter(
      (row) => row.status === "AutoMatched"
    ).length;
    const manualMatched = rows.filter(
      (row) => row.status === "ManualMatched"
    ).length;
    // const locallyMatched = matched - oneKeyMatched;
    const locallyMatched = rows.filter(
      (row) => row.status === "LocalMatched"
    ).length;
    const validationPending = rows.filter(
      (row) => row.onekeyStatus === "PENDING"
    ).length;
    // const unmatched = rows.filter(
    //   (row) =>
    //     row.onekeyMatched === "False" &&
    //     row.onekeyStatus === "NOTFOUND"
    // ).length;
    const unmatched = rows.filter(
      (row) => row.onekeyMatched === "False"
    ).length;

    setLocallyMatchedCount(locallyMatched);
    setOneKeyMatchedCount(oneKeyMatched);
    setManualMatchedCount(manualMatched);
    setValidationPendingCount(validationPending);
    setUnmatchedCount(unmatched);
  }, [rows]);

  useEffect(() => {
    dispatch(setSelectedRecords(rowSelectionModel));
  }, [rowSelectionModel, dispatch]);

  const columnsWithPageScopedSelectAll = useMemo(
    () => [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderHeader: () => <PageSelectAllCheckboxHeader />,
      },
      ...columns,
    ],
    [columns]
  );

  return (
    <>
      <Box sx={{ height: "500px", width: "96%", marginTop: "20px" }}>
        <Box
          sx={{
            padding: "10px",
            backgroundColor: "#f0f0f0",
            borderRadius: "5px",
            marginBottom: "5px",
          }}
        >
          <Typography
            variant="h7"
            sx={{
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tooltip title="Records matched after local matching">
              <span>Local Matched : {locallyMatchedCount}</span>
            </Tooltip>
            <Tooltip title="Records matched after being sent to Onekey">
              <span>Auto Matched : {oneKeyMatchedCount}</span>
            </Tooltip>
            <Tooltip title="Records matched after Manual Matching">
              <span>Manual Matched : {manualMatchedCount}</span>
            </Tooltip>
            <Tooltip title="Records with Onekey Validation Status as 'pending'">
              <span>Validation Pending : {validationPendingCount}</span>
            </Tooltip>
            <Tooltip title="Unmatched Records">
              <span>Unmatched : {unmatchedCount}</span>
            </Tooltip>
          </Typography>
        </Box>
        <DataGrid
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              rows,
              columns,
              handleClickOpen,
              handleGetOneKeyStatus,
              handleRefreshRecords,
              completeJob,
              validateAddress,
              sendAllJobs
            },
          }}
          rowHeight={50}
          rows={rows}
          columns={columnsWithPageScopedSelectAll}
          pagination
          paginationMode="client"
          sx={{
            "& .MuiDataGrid-row": {
              backgroundColor: "dadfe1",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#dee3eb",
              color: "#ab1368",
              fontSize: 16,
            },
            overflowX: "scroll",
            border: "2px solid #ab1368",
            borderRadius: "5px",
          }}
          initialState={{
            density: "compact",
            columns: {
              columnVisibilityModel: columnVisibilityModel,
            },
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          pageSizeOptions={[25, 50, 75, 100]}
          disableRowSelectionOnClick
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
        />
      </Box>
    </>
  );
};

export default RecordDisplayGrid;
